import {
  Box, Typography, Chip, Stack, Slider, Tabs, Tab, Badge,
  TextField, IconButton, Alert, Tooltip, Button, Snackbar,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { GridColDef, GridRenderCellParams, GridRowSelectionModel } from "@mui/x-data-grid";
import { useState, useMemo, useCallback } from "react";
import { useQuery } from "convex/react";
import { useNavigate } from "react-router-dom";
import { useStableQuery } from "../hooks/useStableQuery";
import { useAllPages } from "../hooks/useAllPages";
import { api } from "../../../convex/_generated/api";
import { useMsal } from "@azure/msal-react";
import { DeleteTransactionsModal } from "../components/DeleteTransactionsModal";
import { useCycleFilter } from "../cycleFilterContext";
import { QbPostedBadge } from "../components/qb/QbPostedBadge";
import { HowToFindModal } from "../components/qb/HowToFindModal";
import type { QbAwareTransactionRow } from "../types/qb-aware";
import { DataTableShell } from "../components/table/DataTableShell";
import { CountingFilterSelect } from "../components/table/CountingFilterSelect";
import { DocumentCompareDrawer } from "../components/viewer/DocumentCompareDrawer";
import { AiDisclaimer } from "../components/AiDisclaimer";
import {
  computeStage,
  STAGE_DEFS,
  STAGE_ORDER,
  type StageKey,
  type Stage,
} from "../utils/transactionStage";
import {
  QB_TYPE_DEFS,
  QB_TYPE_TAB_ORDER,
  type QbTypeKey,
} from "../utils/qbTypeAliases";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Build the column set for the Transactions table.
 *
 * v3.0.38 simplification: the previous 4 status columns (QB Import, Posting,
 * Match, Where in QB) + 4 independent filter dropdowns proved overwhelming —
 * the operator had to mentally compose 5 enums per row to understand the
 * operational state. We collapse them into ONE derived `Stage` column +
 * 1 filter, and keep "Where in QB" only for rows actually in QBD (charge/
 * check/etc. badge + how-to-find help button). Raw status fields are still
 * available via the row-click drawer for audit / debug.
 */
/**
 * Human label for the "Where in QB" column — derived from the SAME stage the
 * row already computed, so the column is coherent with Stage (and the
 * 2026-06-18 invoice-gate rule) and is sortable / value-filterable. Mirrors
 * the chips rendered in the qbWhere renderCell.
 */
function qbWhereLabel(row: any): string {
  if (row?.qbTransactionId) {
    return row?.qbDriftDetected === true ? "Drift" : "In QBD";
  }
  const stageKey = (row?.stage as Stage | undefined)?.key;
  switch (stageKey) {
    case "review": return "Review queue";
    case "needs_invoice": return "Awaiting invoice";
    case "duplicate": return "Duplicate — not posting";
    case "failed": return "Post failed";
    case "reversed": return "Reversed";
    case "auto_flow":
    case "ready": return "Queued to post";
    default: return "Not in QB yet";
  }
}

function buildColumns(
  onShowHowTo: (row: QbAwareTransactionRow) => void,
  onStageAction: (stage: Stage) => void,
  onDeleteRow?: (row: any) => void,
): GridColDef[] {
  return [
  {
    field: "date",
    headerName: "Date",
    width: 110,
    sortable: true,
    renderCell: (params: GridRenderCellParams) => formatDate(params.value),
  },
  {
    field: "description",
    headerName: "Description",
    flex: 2,
    minWidth: 220,
    sortable: true,
    renderCell: (params: GridRenderCellParams) => (
      <Typography
        variant="body2"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
        title={params.value ?? ""}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: "vendorName",
    headerName: "Vendor",
    flex: 1,
    minWidth: 140,
    sortable: true,
    renderCell: (params: GridRenderCellParams) => {
      if (!params.value) {
        return <Typography variant="body2" sx={{ color: "var(--c-text-secondary)" }}>—</Typography>;
      }
      return <Typography variant="body2">{params.value}</Typography>;
    },
  },
  {
    field: "amount",
    headerName: "Amount",
    width: 120,
    sortable: true,
    align: "right",
    headerAlign: "right",
    renderCell: (params: GridRenderCellParams) => {
      const val = params.value as number;
      const positive = val >= 0;
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: positive ? "var(--c-metric-success)" : "var(--c-text-primary)",
          }}
        >
          {formatCurrency(val)}
        </Typography>
      );
    },
  },
  {
    // === The unified Stage chip — replaces 4 status columns + 4 filters. ===
    field: "stage",
    headerName: "Stage",
    width: 170,
    sortable: true,
    // MUI X DataGrid v8 valueGetter signature is (value, row) — NOT (params).
    // The old (params)=>params.row.stage form silently returned undefined on
    // v8, which is why Stage didn't sort or value-filter. Return the human
    // label so sort groups like-stages together and the column filter matches
    // the text the operator sees.
    valueGetter: (_value: any, row: any) => (row?.stage as Stage | undefined)?.label ?? "",
    renderCell: (params: GridRenderCellParams) => {
      const stage = (params.row as any).stage as Stage | undefined;
      if (!stage) return null;
      const chip = (
        <Chip
          size="small"
          color={stage.color}
          icon={<span style={{ fontSize: 12, paddingLeft: 6 }}>{stage.icon}</span>}
          label={stage.label}
          onClick={
            stage.actionRoute
              ? (e) => {
                  e.stopPropagation();
                  onStageAction(stage);
                }
              : undefined
          }
          sx={{
            cursor: stage.actionRoute ? "pointer" : "default",
            fontWeight: 500,
          }}
        />
      );
      return <Tooltip title={stage.tooltip}>{chip}</Tooltip>;
    },
  },
  {
    field: "qbWhere",
    headerName: "Where in QB",
    width: 180,
    sortable: true,
    // v8 valueGetter (value, row): textual state so the column sorts and
    // value-filters (was sortable:false with no getter — neither worked).
    valueGetter: (_value: any, row: any) => qbWhereLabel(row),
    renderCell: (params: GridRenderCellParams) => {
      const row = params.row as any;
      if (!row.qbTransactionId) {
        // Not in QBD yet — surface WHERE the row currently sits so the cell is
        // never confusingly blank. Derive from the row's STAGE (the canonical
        // state incl. the 2026-06-18 invoice gate), so this column never
        // disagrees with the Stage chip — a flagged row WITHOUT an invoice now
        // reads "Awaiting invoice" here too, not "Review queue".
        const stageKey = (row.stage as Stage | undefined)?.key;
        const inReviewQueue = stageKey === "review";
        if (inReviewQueue) {
          return (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Review queue"
              onClick={(e) => {
                e.stopPropagation();
                onStageAction({ actionRoute: "/review-queue" } as Stage);
              }}
              sx={{ cursor: "pointer" }}
            />
          );
        }
        if (stageKey === "duplicate")
          return <Chip size="small" variant="outlined" label="Duplicate — not posting" />;
        if (stageKey === "failed")
          return <Chip size="small" color="error" variant="outlined" label="Post failed" />;
        if (stageKey === "reversed")
          return <Chip size="small" variant="outlined" label="Reversed" />;
        if (stageKey === "needs_invoice")
          return (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Awaiting invoice"
              onClick={(e) => { e.stopPropagation(); onStageAction({ actionRoute: "/matches" } as Stage); }}
              sx={{ cursor: "pointer" }}
            />
          );
        if (stageKey === "auto_flow" || stageKey === "ready")
          return <Chip size="small" color="info" variant="outlined" label="Queued to post" />;
        return (
          <Typography variant="caption" color="text.secondary">
            Not in QB yet
          </Typography>
        );
      }
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <QbPostedBadge
            qbTransactionId={row.qbTransactionId}
            qb={row.qb}
            txDate={row.date}
            // Only a CONFIRMED drift (qbDriftDetected — QB verified the row is
            // gone) shows the red "Drift" badge. A row that is merely absent
            // from the local cache (row.qbDrift true via qb===null) is NOT a
            // drift — it falls through to the grey "Posted" badge. This keeps
            // the badge coherent with computeStage, which also keys on
            // qbDriftDetected (so stage "in_qbd" no longer contradicts a red
            // "Drift" chip for the ~308 not-yet-cached posted rows).
            drift={row.qbDriftDetected === true}
          />
          {row.qb && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onShowHowTo(row);
              }}
              title="How to find in QuickBooks Desktop"
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      );
    },
  },
  {
    field: "matchStatus",
    headerName: "Match",
    width: 150,
    sortable: true,
    // Text labels (not symbols) so the column is filterable/searchable by the
    // status word. valueGetter drives sort + (quick)filter; renderCell styles it.
    // MUI X v8 signature: (value, row) — was (params)=>params.row (broke on v8).
    valueGetter: (_value: any, row: any) => {
      const v = row?.matchStatus as string | undefined;
      if (v === "matched") return "Matched";
      if (v === "exception") return "Exception";
      if (v === "human_review") return "Human review";
      if (v === "unmatched") return "Unmatched";
      return v ?? "—";
    },
    renderCell: (params: GridRenderCellParams) => {
      const v = (params.row as any).matchStatus as string | undefined;
      if (v === "matched") return <Chip size="small" color="success" label="Matched" />;
      if (v === "exception") return <Chip size="small" color="error" variant="outlined" label="Exception" />;
      if (v === "human_review") return <Chip size="small" color="primary" variant="outlined" label="Human review" />;
      if (v === "unmatched") return <Chip size="small" variant="outlined" label="Unmatched" />;
      return <Typography variant="caption" color="text.secondary">—</Typography>;
    },
  },
  {
    field: "category",
    headerName: "Category",
    width: 150,
    sortable: true,
    renderCell: (params: GridRenderCellParams) => {
      if (!params.value) {
        return <Typography variant="body2" sx={{ color: "var(--c-text-secondary)" }}>—</Typography>;
      }
      return <Typography variant="body2">{params.value}</Typography>;
    },
  },
  // Trash icon — only present when canDelete (onDeleteRow is defined)
  ...(onDeleteRow
    ? [
        {
          field: "__delete",
          headerName: "",
          width: 48,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params: GridRenderCellParams) => (
            <Tooltip title="Delete transaction">
              <IconButton
                size="small"
                aria-label="Delete transaction"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteRow(params.row);
                }}
                sx={{ color: "error.main", opacity: 0.7, "&:hover": { opacity: 1 } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        } as GridColDef,
      ]
    : []),
  ];
}

export function Transactions() {
  const navigate = useNavigate();
  const [stageFilter, setStageFilter] = useState<StageKey | "all">("all");
  const [typeTab, setTypeTab] = useState<QbTypeKey>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [amountRange, setAmountRange] = useState<number[]>([0, 0]);
  const [amountRangeTouched, setAmountRangeTouched] = useState(false);
  const [modalTarget, setModalTarget] = useState<QbAwareTransactionRow | null>(null);
  const [compareTxnId, setCompareTxnId] = useState<string | null>(null);
  const { cycleArg } = useCycleFilter();

  // --- MSAL identity + role (mirror CardsAdminPage.tsx) ---
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const account = activeAccount ?? accounts[0];
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const claimStr = (k: string) =>
    typeof claims[k] === "string" ? (claims[k] as string).trim() : "";
  const email =
    (account?.username && account.username.trim()) ||
    claimStr("preferred_username") ||
    claimStr("upn") ||
    claimStr("email") ||
    "";
  const roleEmailCandidates = [
    account?.username ?? "",
    claimStr("preferred_username"),
    claimStr("upn"),
    claimStr("email"),
  ].filter((e) => e && e.includes("@"));
  const userRecord = useQuery(
    api.users.findByAnyEmail,
    roleEmailCandidates.length > 0 ? { emails: roleEmailCandidates } : "skip",
  );
  const role = userRecord?.role;
  const canDelete = role === "admin" || role === "accountant";

  // --- Selection state (MUI X DataGrid v8: { type, ids: Set }) ---
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  });

  // --- Delete modal state ---
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState<any[]>([]);

  // --- Snackbar state ---
  const [snack, setSnack] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });
  const showSnack = useCallback((message: string) => {
    setSnack({ open: true, message });
  }, []);
  const handleCloseSnack = useCallback(() => {
    setSnack((prev) => ({ ...prev, open: false }));
  }, []);

  const docs = useQuery(
    api.transactions.getDocumentsForCompare,
    compareTxnId ? { id: compareTxnId as any } : "skip",
  );

  // Client request 2026-10-16: show EVERY transaction, not the first 500.
  // Paginated server-side; all pages load, each call stays bounded.
  const { results: pagedRows, loading: pageLoading, loadingMore } = useAllPages(
    api.transactions.listPaginatedWithQb,
    { ...cycleArg },
  );
  const data = pageLoading ? undefined : pagedRows;

  // The stage-filter counts must reflect ALL transactions in scope, not just
  // the first TX_LIMIT loaded for the list. Otherwise, once a cycle exceeds
  // TX_LIMIT, the filter bar would undercount (the bug class the user flagged).
  // This lightweight query carries no limit and no QB join.
  const stageFieldRows = useStableQuery(
    api.transactions.listStageFields,
    { ...cycleArg },
    [],
  );

  const handleShowHowTo = useCallback(
    (row: QbAwareTransactionRow) => setModalTarget(row),
    [],
  );
  const handleStageAction = useCallback(
    (stage: Stage) => {
      if (stage.actionRoute) navigate(stage.actionRoute);
    },
    [navigate],
  );
  const handleDeleteRow = useCallback((row: any) => {
    setDeleteTargets([row]);
    setDeleteOpen(true);
  }, []);

  const columns = useMemo(
    () => buildColumns(
      handleShowHowTo,
      handleStageAction,
      canDelete ? handleDeleteRow : undefined,
    ),
    [handleShowHowTo, handleStageAction, canDelete, handleDeleteRow],
  );

  const loading = data === undefined;
  const allRowsRaw: any[] = data ?? [];

  // Hydrate each row with its computed stage so columns, filters and KPI
  // cards all share one source of truth.
  const allRows = useMemo(
    () => allRowsRaw.map((tx) => ({ ...tx, stage: computeStage(tx) })),
    [allRowsRaw],
  );

  const maxAmount = useMemo(() => {
    if (allRows.length === 0) return 0;
    return Math.ceil(Math.max(...allRows.map((t: any) => Math.abs(t.amount ?? 0))));
  }, [allRows]);

  const effectiveAmountRange = amountRangeTouched ? amountRange : [0, maxAmount];

  // Per-stage counts so the filter dropdown shows live numbers.
  const stageCounts = useMemo(() => {
    // Every StageKey must be seeded here. `skipped` and `matching` were added
    // to the union (de020e8, "Matching" stage) without updating this literal,
    // which broke `tsc -p src/frontend/tsconfig.json` and therefore the whole
    // `npm run build` (tsc && vite build) — the frontend dist had been frozen
    // at 2026-06-25 ever since. 2026-07-27.
    const counts: Record<StageKey, number> = {
      new: 0, skipped: 0, auto_flow: 0, needs_invoice: 0, matching: 0,
      ready: 0, review: 0, in_qbd: 0, drift: 0, duplicate: 0,
      failed: 0, reversed: 0,
    };
    // Count over the FULL set (stageFieldRows, unpaginated) so the filter bar is
    // coherent even when the cycle exceeds TX_LIMIT. Fall back to the loaded
    // rows only while the count query is still in flight.
    const source = stageFieldRows ?? allRows;
    for (const tx of source) counts[computeStage(tx as any).key]++;
    return counts;
  }, [stageFieldRows, allRows]);

  // Per-type counts for the tabs. txnType comes from the qbTransactions
  // cache join; rows without `qb` haven't posted yet → "not_posted" bucket.
  const typeCounts = useMemo(() => {
    const counts: Record<QbTypeKey, number> = {
      all: 0,
      CreditCardCharge: 0, CreditCardCredit: 0,
      Check: 0, Deposit: 0, Bill: 0, JournalEntry: 0,
      not_posted: 0,
    };
    for (const tx of allRows) {
      counts.all++;
      const t = (tx.qb?.txnType ?? null) as QbTypeKey | null;
      if (!t) counts.not_posted++;
      else if (t in counts) counts[t]++;
    }
    return counts;
  }, [allRows]);

  const filtered = useMemo(() => {
    return allRows.filter((tx: any) => {
      if (stageFilter !== "all" && (tx.stage as Stage).key !== stageFilter) return false;
      if (typeTab !== "all") {
        const t = tx.qb?.txnType as QbTypeKey | undefined;
        if (typeTab === "not_posted") {
          if (t) return false;
        } else {
          if (t !== typeTab) return false;
        }
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const desc = tx.description?.toLowerCase() ?? "";
        const vendor = tx.vendorName?.toLowerCase() ?? "";
        const ref = tx.reference?.toLowerCase() ?? "";
        if (!desc.includes(term) && !vendor.includes(term) && !ref.includes(term)) return false;
      }
      const absAmount = Math.abs(tx.amount ?? 0);
      if (absAmount < effectiveAmountRange[0] || absAmount > effectiveAmountRange[1]) return false;
      return true;
    });
  }, [allRows, stageFilter, typeTab, searchTerm, effectiveAmountRange]);

  const rows = filtered.map((tx: any) => ({ ...tx, id: tx._id }));

  // Derive selected rows from the v8 GridRowSelectionModel {type, ids: Set}.
  // - type="include": selected = rows whose _id is in ids (normal checkbox select)
  // - type="exclude": selected = rows whose _id is NOT in ids (select-all then deselect)
  // We always operate on the currently-filtered rows so stage/type/search filters are respected.
  const selectedRows = useMemo(() => {
    if (!canDelete) return [];
    const { type, ids } = selectionModel as { type: "include" | "exclude"; ids: Set<string> };
    if (type === "include") {
      if (ids.size === 0) return [];
      return rows.filter((r) => ids.has(r._id as string));
    }
    // exclude: every filtered row NOT in the excluded-ids set
    return rows.filter((r) => !ids.has(r._id as string));
  }, [selectionModel, rows, canDelete]);

  const resetFilters = () => {
    setStageFilter("all");
    setTypeTab("all");
    setSearchTerm("");
    setAmountRangeTouched(false);
  };

  const activeTypeDef = QB_TYPE_DEFS[typeTab];

  return (
    <>
      <AiDisclaimer context="Vendors, categories, matches and postings here are AI-generated and can contain errors" />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DeleteSweepIcon />}
          onClick={() => navigate("/transactions/trash")}
          sx={{ textTransform: "none", fontSize: "0.8rem" }}
        >
          Trash
        </Button>
      </Box>
      <Box sx={{ mb: 2 }} data-tour="transactions-type-tabs">
        <Tabs
          value={typeTab}
          onChange={(_, v) => setTypeTab(v as QbTypeKey)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 52,
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              minHeight: 52,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              gap: 1,
              px: 2,
            },
            "& .MuiTabs-indicator": {
              height: 3,
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
              backgroundColor: activeTypeDef.color,
            },
          }}
        >
          {QB_TYPE_TAB_ORDER.map((k) => {
            const def = QB_TYPE_DEFS[k];
            const count = typeCounts[k];
            return (
              <Tab
                key={k}
                value={k}
                disableRipple
                icon={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: typeTab === k ? def.color : "text.secondary",
                    }}
                  >
                    <def.Icon fontSize="small" />
                  </Box>
                }
                iconPosition="start"
                aria-label={`${def.label} — ${def.description}`}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{def.label}</span>
                    <Badge
                      badgeContent={count}
                      max={9999}
                      color="default"
                      sx={{
                        "& .MuiBadge-badge": {
                          position: "static",
                          transform: "none",
                          backgroundColor:
                            typeTab === k ? def.color : "var(--c-surface-2, rgba(255,255,255,0.06))",
                          color:
                            typeTab === k ? "#fff" : "var(--c-text-secondary, #888)",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          height: 18,
                          minWidth: 22,
                          px: 0.75,
                          borderRadius: 9,
                        },
                      }}
                    />
                  </Stack>
                }
              />
            );
          })}
        </Tabs>
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: "text.secondary", pl: 0.5 }}
        >
          {activeTypeDef.description}
        </Typography>
      </Box>

      <DataTableShell
        title="Transactions"
        subtitle="Imported bank statement transactions. The Stage column tells you what the row needs."
        data-tour="page-title-transactions"
        rows={rows as any}
        columns={columns}
        loading={loading}
        totalBeforeFilters={allRows.length}
        itemLabel="transaction"
        emptyTitle="No transactions in this cycle"
        emptyDescription="Import bank statements to get started, or switch cycle from the top bar."
        emptyIcon={<ReceiptLongIcon sx={{ fontSize: 32, color: "var(--c-link-accent)" }} />}
        onResetFilters={resetFilters}
        notice={
          loadingMore ? (
            <Alert severity="info">
              Loading all transactions… {allRows.length} so far.
            </Alert>
          ) : undefined
        }
        headerExtra={
          canDelete && selectedRows.length > 0 ? (
            <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
              <Button
                color="error"
                variant="outlined"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  setDeleteTargets(selectedRows);
                  setDeleteOpen(true);
                }}
              >
                Delete ({selectedRows.length})
              </Button>
            </Box>
          ) : undefined
        }
        gridProps={{
          initialState: {
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "date", sort: "desc" }] },
          },
          onRowClick: (params) => setCompareTxnId(String((params.row as any)._id)),
          sx: { "& .MuiDataGrid-row": { cursor: "pointer" } },
          // --- Checkbox selection (accountant/admin only) ---
          ...(canDelete && {
            checkboxSelection: true,
            rowSelectionModel: selectionModel,
            onRowSelectionModelChange: (m) => setSelectionModel(m),
            keepNonExistentRowsSelected: true,
          }),
        }}
        filterBar={
          <>
            <CountingFilterSelect
              label="Stage"
              value={stageFilter}
              onChange={(v) => setStageFilter(v as StageKey | "all")}
              allLabel="All Stages"
              options={STAGE_ORDER.map((k) => ({
                value: k,
                label: `${STAGE_DEFS[k].icon} ${STAGE_DEFS[k].label}`,
                count: stageCounts[k],
              }))}
            />
            <TextField
              size="small"
              label="Search"
              placeholder="Description, vendor or reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 280 }}
            />
            <Stack direction="column" spacing={0.5} sx={{ minWidth: 260, flex: 1, maxWidth: 420 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                Amount: {formatCurrency(effectiveAmountRange[0])} – {formatCurrency(effectiveAmountRange[1])}
              </Typography>
              <Slider
                value={effectiveAmountRange}
                onChange={(_e, newValue) => {
                  setAmountRangeTouched(true);
                  setAmountRange(newValue as number[]);
                }}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => formatCurrency(v)}
                min={0}
                max={maxAmount > 0 ? maxAmount : 100}
                size="small"
                sx={{
                  color: "#6c5ce7",
                  "& .MuiSlider-thumb": { width: 14, height: 14 },
                }}
              />
            </Stack>
          </>
        }
      />

      <DocumentCompareDrawer
        open={Boolean(compareTxnId)}
        onClose={() => setCompareTxnId(null)}
        title="Document Comparison"
        subtitle={(() => {
          const row = compareTxnId
            ? allRows.find((t: any) => t._id === compareTxnId)
            : null;
          if (!row) return undefined;
          return `${row.vendorName ?? row.description ?? ""} — ${formatCurrency(Math.abs(row.amount ?? 0))}`;
        })()}
        left={{
          storageId: docs?.bankStorageId ?? null,
          label: docs?.bankFileName
            ? `Bank Statement · ${docs.bankFileName}`
            : "Bank Statement",
        }}
        right={{
          storageId: docs?.invoiceStorageId ?? null,
          label: docs?.invoiceVendorName
            ? `Invoice · ${docs.invoiceVendorName}${docs.invoiceNumber ? ` #${docs.invoiceNumber}` : ""}`
            : "Invoice",
        }}
        editTarget={
          compareTxnId ? { type: "transaction", id: compareTxnId } : null
        }
      />

      {modalTarget && (
        <HowToFindModal
          open={Boolean(modalTarget)}
          onClose={() => setModalTarget(null)}
          txDate={modalTarget.date}
          qb={modalTarget.qb}
        />
      )}

      <DeleteTransactionsModal
        open={deleteOpen}
        rows={deleteTargets}
        deletedBy={email}
        onClose={() => setDeleteOpen(false)}
        onDone={(msg: string) => {
          setSelectionModel({ type: "include", ids: new Set() });
          showSnack(msg);
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={snack.message}
      />
    </>
  );
}
